import { useState, useEffect, useCallback, useRef, memo } from "react";

interface Slide {
  id: number;
  image: string;
  subtitle: string;
  subtitleZh: string;
  title: string;
  titleZh: string;
  description: string;
  descriptionZh: string;
}

const slides: Slide[] = [
  {
    id: 1,
    image: "https://static.readdy.ai/image/c8608e961562513b854fa363830f8188/cea7957f14a15667f0e96548d693a921.jpeg",
    subtitle: "RESIDENTIAL",
    subtitleZh: "住宅",
    title: "SPACE\nAS POETRY",
    titleZh: "空间如诗",
    description: "Crafting spaces that breathe — where structure meets soul.",
    descriptionZh: "打造会呼吸的空间——结构遇见灵魂之处",
  },
  {
    id: 2,
    image: "https://public.readdy.ai/ai/img_res/f8785eab12fdf01de4d70cd73d4921ab.jpg",
    subtitle: "COMMERCIAL",
    subtitleZh: "商业",
    title: "LIGHT\nDEFINES PLACE",
    titleZh: "光塑空间",
    description: "Light defines place — shaping atmosphere, guiding movement, revealing form.",
    descriptionZh: "光塑造空间——定义氛围、引导动线、呈现形态",
  },
  {
    id: 3,
    image: "https://readdy.ai/api/search-image?query=High-end%20commercial%20interior%20space%20design%20with%20open-plan%20layout%2C%20polished%20concrete%20floors%2C%20exposed%20steel%20structural%20elements%20matte%20black%2C%20warm%20pendant%20lighting%20arrangement%2C%20seamless%20integration%20of%20industrial%20and%20modern%20aesthetics%2C%20professional%20architectural%20and%20interior%20photography%2C%20sophisticated%20atmospheric%20space&width=1920&height=1080&seq=3&orientation=landscape",
    subtitle: "CULTURAL",
    subtitleZh: "文化",
    title: "MATERIAL\nAND MEANING",
    titleZh: "材质与意义",
    description: "Where material, light and geometry converge into meaning.",
    descriptionZh: "材质、光线与几何在此汇聚成意义",
  },
];

function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  // Pause auto-play when hero is not visible (user scrolled away)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (animating) return;
      setAnimating(true);
      setCurrent(index);
      setTimeout(() => setAnimating(false), 800);
    },
    [animating]
  );

  const goNext = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, goTo]);

  const goPrev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length);
  }, [current, goTo]);

  useEffect(() => {
    if (!isVisible) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(goNext, 5500);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [goNext, isVisible]);

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(goNext, 5500);
  };

  const handlePrev = () => {
    goPrev();
    resetTimer();
  };

  const handleNext = () => {
    goNext();
    resetTimer();
  };

  const handleDot = (i: number) => {
    goTo(i);
    resetTimer();
  };

  return (
    <section id="hero" ref={sectionRef} className="relative w-full h-screen overflow-hidden">
      {slides.map((slide, idx) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            idx === current ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
          aria-hidden={idx !== current}
        >
          {/* Only render non-active slide images when they've been viewed or are adjacent */}
          {(idx === 0 || Math.abs(idx - current) <= 1 || idx === slides.length - 1) && (
            <img
              src={slide.image}
              alt=""
              aria-hidden="true"
              width={1920}
              height={1080}
              loading={idx === 0 ? "eager" : "lazy"}
              fetchPriority={idx === 0 ? "high" : "low"}
              decoding={idx === 0 ? "sync" : "async"}
              className={`absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[8000ms] ease-linear ${
                idx === current ? "scale-110" : "scale-100"
              }`}
            />
          )}
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-20 flex flex-col items-center justify-center h-full w-full text-center px-8">
        <p
          className="text-xs tracking-[0.4em] text-white/60 mb-6 font-medium"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {slides[current].subtitle} · {slides[current].subtitleZh}
        </p>
        <h2
          className="text-6xl lg:text-8xl font-black text-white leading-none mb-2 whitespace-pre-line"
          style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "-0.02em" }}
        >
          {slides[current].title}
        </h2>
        <p className="text-2xl lg:text-3xl font-light text-white/80 mb-6 tracking-widest"
           style={{ fontFamily: "'Noto Serif SC', serif" }}>
          {slides[current].titleZh}
        </p>
        <p className="text-sm text-white/70 tracking-widest max-w-md font-light mb-1"
           style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {slides[current].description}
        </p>
        <p className="text-xs text-white/50 tracking-widest max-w-md font-light italic"
           style={{ fontFamily: "'Noto Sans SC', sans-serif" }}
        >
          {slides[current].descriptionZh}
        </p>
      </div>

      {/* Prev / Next Arrows */}
      <button
        onClick={handlePrev}
        className="absolute left-6 lg:left-10 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center border border-white/40 text-white hover:bg-white/20 transition-all duration-300 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-lg" />
      </button>
      <button
        onClick={handleNext}
        className="absolute right-6 lg:right-10 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center border border-white/40 text-white hover:bg-white/20 transition-all duration-300 cursor-pointer"
      >
        <i className="ri-arrow-right-line text-lg" />
      </button>

      {/* Dots + Counter */}
      <div className="absolute bottom-10 left-0 right-0 z-20 flex flex-col items-center gap-4">
        <div className="flex items-center gap-3">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => handleDot(i)}
              className={`transition-all duration-300 cursor-pointer rounded-full ${
                i === current ? "w-8 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
        <p className="text-xs text-white/40 tracking-[0.3em]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {String(current + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")} · 共 {slides.length} 张
        </p>
      </div>

      {/* Scroll Hint */}
      <div className="absolute bottom-10 right-10 z-20 flex flex-col items-center gap-2">
        <p className="text-[10px] text-white/40 tracking-[0.3em] rotate-90 origin-center mb-4"
           style={{ fontFamily: "'DM Sans', sans-serif" }}>
          SCROLL · 滚动
        </p>
        <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent animate-pulse" />
      </div>
    </section>
  );
}

export default memo(HeroCarousel);
